import { useEffect, useState } from 'react';
import { Menu, Cookie } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router-dom';
import * as CookieConsent from 'vanilla-cookieconsent';
import logo from '../assets/Logo_Staisch_innovative_Elektrotechnik.png';
import { cn } from '../lib/utils';
import { ModeToggle } from './ModeToggle';
import { LanguageSwitcher } from './LanguageSwitcher';
import {
  NavigationMenu,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuViewport
} from '@radix-ui/react-navigation-menu';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';

const navigationItems = [
  { to: '/', labelKey: 'nav.home' },
  { to: '/leistungen', labelKey: 'nav.services' },
  { to: '/unternehmen', labelKey: 'nav.company' },
  { to: '/referenzen', labelKey: 'nav.references' },
  { to: '/kontakt', labelKey: 'nav.contact' }
] as const;

export const Header = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    setIsNavOpen(false);
  }, [pathname]);

  return (
    <header className="site-header">
      <div className="container">
        <Link className="flex items-center gap-3" to="/" aria-label="Zur Startseite">
          <img src={logo} alt="Staisch Innovative Elektrotechnik" className="brand__logo" />
        </Link>
        <div className="hidden min-w-0 flex-1 items-center justify-end gap-4 md:flex">
          <nav aria-label="Hauptnavigation">
            <NavigationMenu className="mr-2 min-w-0">
            <NavigationMenuList className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 rounded-full border border-slate-200 bg-white/90 px-2 py-1 shadow-md backdrop-blur dark:border-slate-700 dark:bg-slate-900/80">
              {navigationItems.map((item) => (
                <NavigationMenuItem key={item.to}>
                  <NavigationMenuLink asChild>
                    <Link
                      to={item.to}
                      aria-current={pathname === item.to ? 'page' : undefined}
                      className={cn(
                        'nav-link inline-flex h-10 items-center rounded-full px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:text-slate-100 dark:hover:bg-slate-800 lg:px-4 whitespace-nowrap',
                        pathname === item.to &&
                          'bg-blue-600 text-white drop-shadow-[0_1px_0_rgba(0,0,0,0.25)] hover:text-white focus:text-white focus-visible:text-white focus-visible:ring-offset-blue-600 dark:bg-blue-500 dark:text-white'
                      )}
                    >
                      {t(item.labelKey)}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              ))}
              <NavigationMenuIndicator className="hidden" />
            </NavigationMenuList>
            <NavigationMenuViewport className="hidden" />
          </NavigationMenu>
          </nav>
          <LanguageSwitcher />
          <ModeToggle />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => CookieConsent.showPreferences()}
            className="rounded-full border border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Cookie-Einstellungen"
          >
            <Cookie className="h-4 w-4" />
          </Button>
        </div>
        <div className="md:hidden">
          <Sheet open={isNavOpen} onOpenChange={setIsNavOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                aria-expanded={isNavOpen}
                aria-controls="mobile-navigation"
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">Menü öffnen</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-white p-6 dark:bg-slate-900">
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation</SheetTitle>
                <SheetDescription>Hauptnavigation der Staisch Innovative Elektrotechnik Website</SheetDescription>
              </SheetHeader>
              <div className="flex items-center justify-between">
                <img src={logo} className="h-14 w-auto" alt="Staisch Innovative Elektrotechnik" />
              </div>
              <nav
                id="mobile-navigation"
                className="mt-8 grid gap-3 text-base font-medium text-slate-700"
                aria-label="Hauptnavigation"
              >
                {navigationItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        'rounded-md px-3 py-2 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800',
                        isActive && 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-200'
                      )
                    }
                  >
                    {t(item.labelKey)}
                  </NavLink>
                ))}
              </nav>
              <div className="mt-6 flex items-center gap-4">
                <ModeToggle />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => CookieConsent.showPreferences()}
                  className="rounded-full border border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                  aria-label="Cookie-Einstellungen"
                >
                  <Cookie className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-4">
                <LanguageSwitcher fullWidth />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
